import React, { useEffect, useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTenant } from "@/lib/TenantContext";
import { useT } from "@/lib/LanguageContext";
// Relative, not "@/...": real modules under Jest, no alias there.
import { resolveBrandName } from "../lib/siteLinks";
import { MARKETING_OPT_IN as IDS } from "../constants/testIds/marketingOptIn";
import Section from "./PortalSection";

const PREFS = "/client/email-preferences";
// One literal key per stored status (the backend's CONSENT_STATUSES), so the key-coverage
// test sees each of them; an unknown status reads as "not receiving".
const STATUS_KEYS = {
  consented: "portalEmail.status.consented",
  not_consented: "portalEmail.status.not_consented",
  unsubscribed: "portalEmail.status.unsubscribed",
};

/**
 * "Email preferences" on the client's account page (owner decision 2026-10-21): the
 * signed-in client's marketing status FOR THIS PRACTICE, and the one control that changes
 * it — `GET/PUT /api/client/email-preferences` (backend modules/crm/consent.py).
 *
 * WHAT THE TWO BUTTONS MEAN. Opting in here is an explicit act of the signed-in client, so
 * it is the one door allowed to lift an earlier unsubscribe; opting out is the ordinary
 * unsubscribe (marketing sequences stop; appointment and purchase emails never do, which
 * the intro says). Nothing is ever chosen for the client: the page only shows the stored
 * answer and offers the other one.
 *
 * Owns its data, like PortalPathways: a refused read — the practice's own owner signed in
 * on their site is not their own client (403) — renders nothing at all, and never costs the
 * rest of the page.
 */
export default function PortalEmailPreferences() {
  const t = useT("public");
  const { tenant } = useTenant();
  const practice = resolveBrandName(tenant, t("marketingOptIn.practiceFallback"));
  const [status, setStatus] = useState(null); // consented | not_consented | unsubscribed
  const [hidden, setHidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(PREFS)
      .then(({ data }) => { if (alive) setStatus(data?.marketing || "not_consented"); })
      .catch(() => { if (alive) setHidden(true); });
    return () => { alive = false; };
  }, []);

  if (hidden || status === null) return null;

  const choose = async (marketing) => {
    setSaving(true);
    setSaveError(false);
    try {
      const { data } = await api.put(PREFS, { marketing });
      setStatus(data?.marketing || status);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  const subscribed = status === "consented";
  return (
    <Section icon={Mail} title={t("portalEmail.title")} testid={IDS.portalSection}>
      <p className="text-sm text-eh-muted">{t("portalEmail.intro", { practice })}</p>
      <p className="mt-3 font-medium text-eh-ink" data-testid={IDS.portalStatus}>
        {t(STATUS_KEYS[status] || STATUS_KEYS.not_consented, { practice })}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {subscribed ? (
          <button
            type="button"
            onClick={() => choose(false)}
            disabled={saving}
            data-testid={IDS.portalOptOut}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-eh-border-soft text-eh-ink hover:border-eh-ink/30 transition disabled:opacity-60"
          >
            {saving && <Loader2 size={14} className="animate-spin" />} {t("portalEmail.optOut")}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => choose(true)}
            disabled={saving}
            data-testid={IDS.portalOptIn}
            className="btn-primary text-sm disabled:opacity-60"
          >
            {saving && <Loader2 size={14} className="animate-spin" />} {t("portalEmail.optIn")}
          </button>
        )}
      </div>
      {saveError && (
        <p role="alert" className="mt-3 text-sm text-red-600" data-testid={IDS.portalError}>
          {t("portalEmail.saveError")}
        </p>
      )}
    </Section>
  );
}
